import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from "react";
import { CaregiverRelationship, Pronouns } from "../__generated__/graphql";

type PatientState = {
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: Date | null;
  state: string | null;
  pronouns: Pronouns | null;
};

type CaregiverState = {
  firstName: string;
  lastName: string;
  dateOfBirth: Date;
  relationshipToPatient: CaregiverRelationship;
};

type PatientContextValue = {
  patient: PatientState;
  setPatient: Dispatch<SetStateAction<PatientState>>;
  caregiver: CaregiverState | null;
  setCaregiver: (caregiverState: CaregiverState) => void;
};

type PatientProviderProps = {
  children: ReactNode;
};

const PatientContext = createContext<PatientContextValue | null>(null);

export const PatientProvider = ({ children }: PatientProviderProps) => {
  const [patient, setPatient] = useState<PatientState>({
    firstName: null,
    lastName: null,
    dateOfBirth: null,
    state: null,
    pronouns: null,
  });
  const [caregiver, stateSetCaregiver] = useState<CaregiverState | null>(null);
  const setCaregiver = (caregiverState: CaregiverState) =>
    stateSetCaregiver(caregiverState);

  const value = useMemo(
    () => ({
      patient,
      setPatient,
      caregiver,
      setCaregiver,
    }),
    [patient, caregiver],
  );

  return (
    <PatientContext.Provider value={value}>{children}</PatientContext.Provider>
  );
};

export const usePatient = () => {
  const patientContext = useContext(PatientContext);

  if (patientContext === null) {
    throw new Error("usePatient must be used within an PatientProvider");
  }

  return patientContext;
};
